/* eslint-disable @typescript-eslint/no-explicit-any */
import { DatasetTypeHandler } from 'app/components/create-datamapper/utils/datasettype-handler';
import { Dataset, IAttribute, LinkedService, Metamodel, Named, Owned } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { IMapperTable, IKafkaMapperEntity, SchemaLoadStatus, IMapperConnectionInfo } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { escapeDelimiter, getValidTransformationName } from 'app/model/adfDataMapper/adfDataMapperUtils';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { QueryApiFactoryService } from 'app/apis/query-api.factory.service';
import { Subject, first, lastValueFrom } from 'rxjs';
import { AdfDataMapper } from 'app/model/adfDataMapper/adfDataMapper';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';
import { KafkaSettings } from 'app/model/dataflow/dataset-settings/kafka-settings';
import { KafkaDataset } from 'app/model/dataset/kafka.dataset';
import { KafkaLinkedService } from 'app/model/linkedservice/kafka.linkedservice';
import { DataFlowExpression, createDataflowExpression } from 'app/model/dataflow/dataflowexpression';

export class KafkaDatasetTypeHandler extends DatasetTypeHandler {
    public supportedFormats = ['json', 'avro', 'delimited'];

    constructor(
        public isSource: Boolean
    ) {
        super();
    }

    public getOkButtonStatus(selectedRows: IKafkaMapperEntity[]): [boolean, string] {
        let disableButton = true;
        let disableButtonStatus = '';
        if (selectedRows.length) {
            disableButton = false;
            const rows = selectedRows as IKafkaMapperEntity[];
            rows.forEach((elem: IKafkaMapperEntity) => {
                disableButton = !(elem.subscribe?.length > 0 || elem.subscribePattern?.length > 0) ||
                (elem.schemaInferenceType === DslConnectorConstants.payloadSchema && !(elem.payloadSchema?.length > 0) ||
                (elem.schemaInferenceType === DslConnectorConstants.autoInferSchema && (elem.numSampleRecords === undefined || elem.numSampleRecords < 1)));
            });
            if (!disableButton) {
                [disableButton, disableButtonStatus] = !this._areAllPathsAreUnique(rows) ? [true, this.isSource ? stringResources.DuplicateSourceEntitySelected : stringResources.DuplicateTargetEntitySelected] : [false, ''];
            }
            return [disableButton, disableButtonStatus];
        }
        return [true, ''];
    }

    public processSelectedRows(selectedRows: IKafkaMapperEntity[], selectedSourceLinkedService: LinkedService, selectedDatasetFormat: DatasetFormatType): IMapperTable[] {
        const tables: IMapperTable[] = [];
        selectedRows.forEach((elem) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const dslConnectorProperties: Map<string, any> = new Map([
                [DslConnectorConstants.format, elem.format]
            ]);
            //subscribePattern and subscribe are mutually exclusive
            if (elem.subscribePattern) {
                dslConnectorProperties.set(DslConnectorConstants.subscribePattern, elem.subscribePattern);
            } else {
                dslConnectorProperties.set(DslConnectorConstants.subscribe, elem.subscribe);
                dslConnectorProperties.set(DslConnectorConstants.subscribe, elem.subscribe);
            }
            if (elem.format !== DslConnectorConstants.passThrough) {
                dslConnectorProperties.set(DslConnectorConstants.schemaInferenceType, elem.schemaInferenceType);
            }
            if (elem.schemaInferenceType === DslConnectorConstants.payloadSchema) {
                dslConnectorProperties.set(DslConnectorConstants.payloadSchema, elem.payloadSchema);
            }
            if (elem.schemaInferenceType === DslConnectorConstants.autoInferSchema) {
                dslConnectorProperties.set(DslConnectorConstants.numSampleRecords, elem.numSampleRecords);
            }
            dslConnectorProperties.set(DslConnectorConstants.includeHeaders, elem.includeHeaders);

            tables.push({
                name: getValidTransformationName(this.isSource, elem.entityName),
                // dataset doesn't exist in UX for now. Will use work-around at places required.
                sourceDataset: undefined,
                sourceLinkedService: selectedSourceLinkedService,
                isInlineDataset: true,
                datasetSettings: undefined,
                wildcardPaths: [],
                dslConnectorProperties,
                datasetType: selectedDatasetFormat,
                schemaLoadStatus: SchemaLoadStatus.NOT_STARTED,
                tableName: elem.entityName
            });
        });

        return tables;
    }

    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        const kafkaSettings = Owned.create<KafkaSettings>(KafkaSettings.MODEL, outputSource);
        const incomingSourceDatasetsSettings = inputSource.datasetSettings as KafkaSettings;
        kafkaSettings.initialize('');
        kafkaSettings.subscribe.expression = !incomingSourceDatasetsSettings.subscribe.expression ? undefined : incomingSourceDatasetsSettings.subscribe.expression;
        kafkaSettings.subscribePattern.expression = !incomingSourceDatasetsSettings.subscribePattern.expression ? undefined : incomingSourceDatasetsSettings.subscribePattern.expression;
        kafkaSettings.instanceSelectionType.expression = kafkaSettings.subscribe.expression ? stringResources.InstanceSelectionType_Manual : stringResources.InstanceSelectionType_Pattern;
        kafkaSettings.format.expression = incomingSourceDatasetsSettings.format.expression;
        kafkaSettings.payloadSchema.expression = !incomingSourceDatasetsSettings.payloadSchema.expression ? undefined : incomingSourceDatasetsSettings.payloadSchema.expression;
        kafkaSettings.isAutoInfer.expression = !incomingSourceDatasetsSettings.isAutoInfer.expression ? undefined : incomingSourceDatasetsSettings.isAutoInfer.expression;
        kafkaSettings.includeHeaders.expression = incomingSourceDatasetsSettings.includeHeaders.expression;
        kafkaSettings.numSampleRecords.expression = !incomingSourceDatasetsSettings.numSampleRecords.expression ? undefined : incomingSourceDatasetsSettings.numSampleRecords.expression;
        if (!this.isSource) {
            kafkaSettings.isSinkSchemaQuery = true;
        }
        outputSource.wildcardPaths = inputSource.wildcardPaths;
        outputSource.datasetSettings = kafkaSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        const kafkaSettings = Owned.create<KafkaSettings>(KafkaSettings.MODEL, table as DataFlowSource);
        kafkaSettings.initialize('');
        kafkaSettings.subscribe.expression = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.subscribe);
        kafkaSettings.subscribePattern.expression = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.subscribePattern);
        kafkaSettings.instanceSelectionType.expression = kafkaSettings.subscribe.expression ? stringResources.InstanceSelectionType_Manual : stringResources.InstanceSelectionType_Pattern;
        kafkaSettings.format.expression = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.format);
        kafkaSettings.payloadSchema.expression = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.payloadSchema);
        kafkaSettings.isAutoInfer = createDataflowExpression(getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.schemaInferenceType) === DslConnectorConstants.autoInferSchema ? 'true' : 'false') as DataFlowExpression;
        kafkaSettings.includeHeaders.expression = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.includeHeaders);
        kafkaSettings.numSampleRecords.expression = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.numSampleRecords);
        return kafkaSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const dataset = new KafkaDataset(Metamodel.modelFor(KafkaDataset).name);
        dataset.linkedService = linkedService;
        return dataset;
    }

    public updateMapperTablePropertiesOnEdit(existingMapperTable: IMapperTable, updatedMapperTable: IMapperTable): void {
        existingMapperTable.datasetSettings = updatedMapperTable.datasetSettings;
        existingMapperTable.dslConnectorProperties = updatedMapperTable.dslConnectorProperties;
    }

    public doReImportSchemaforEditOption(existingMapperTable: IMapperTable, updatedMapperTable: IMapperTable): boolean {
        if (!!existingMapperTable) {
            return existingMapperTable.dslConnectorProperties.get('subscribe') !== updatedMapperTable.dslConnectorProperties.get('subscribePattern')
                || existingMapperTable.dslConnectorProperties.get('subscribePattern') !== updatedMapperTable.dslConnectorProperties.get('subscribePattern')
                || existingMapperTable.dslConnectorProperties.get('format') !== updatedMapperTable.dslConnectorProperties.get('format')
                || existingMapperTable.dslConnectorProperties.get('payloadSchema') !== updatedMapperTable.dslConnectorProperties.get('payloadSchema')
                || existingMapperTable.dslConnectorProperties.get('includeHeaders') !== updatedMapperTable.dslConnectorProperties.get('includeHeaders')
                || existingMapperTable.dslConnectorProperties.get('numSampleRecords') !== updatedMapperTable.dslConnectorProperties.get('numSampleRecords');
        }
        return true;
    }

    public async fetchEntitiesUsingDebugApi(queryApiFactoryService: QueryApiFactoryService, linkedService: LinkedService, isSource: boolean,
        getEntitiesForADataFlowSource: (source: DataFlowSource, resultSubject: Subject<any>) => Promise<void> = undefined): Promise<any[]> {
            const dataMappingModel: AdfDataMapper= new AdfDataMapper('datamapper');
            const dataflow: DataFlow = Named.create<DataFlow>(DataFlow.MODEL, dataMappingModel);
            const source: DataFlowSource = Named.create<DataFlowSource>(DataFlowSource.MODEL, dataflow);
            source.sourceLinkedService = linkedService;
            const resultSubject = new Subject<IKafkaMapperEntity[]>();
            const firstResult = lastValueFrom(resultSubject.pipe(first()));
            const kafkaSettings = Owned.create<KafkaSettings>(KafkaSettings.MODEL, source);
            kafkaSettings.initialize(undefined);
            kafkaSettings.format.expression = 'passthrough';
            kafkaSettings.store = 'kafka';
            kafkaSettings.authenticationType = 'SASL';
            kafkaSettings.saslMechanism = 'PLAIN';
            kafkaSettings.securityProtocol = 'SASL_SSL';
            kafkaSettings.bootstarpsServers = this.valueToResource(linkedService, KafkaLinkedService.BOOTSTRAPSERVERS);
            kafkaSettings.username = linkedService.cachedResource.properties.typeProperties.username;
            kafkaSettings.password = linkedService.cachedResource.properties.typeProperties.encryptedCredential;
            source.datasetSettings = kafkaSettings;
            source.isInlineDataset = true;
            source.name = 'temp';
            dataflow.addSource(source);
            dataflow.name = 'temp';
            source.addOutputStream();
            await getEntitiesForADataFlowSource(source, resultSubject);
            return await firstResult;
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);
        const commonDslConnectorProperties = mapperConnectionInfo.connection.commonDslConnectorProperties;
        commonDslConnectorProperties.set(DslConnectorConstants.store, 'kafka');
        const format = mapperConnectionInfo.tables[0].dslConnectorProperties.get('format');
        if (format === 'delimited') {
            commonDslConnectorProperties.set(DslConnectorConstants.columnNamesAsHeader, true);
            commonDslConnectorProperties.set(DslConnectorConstants.columnDelimiter, escapeDelimiter(','));
            commonDslConnectorProperties.set(DslConnectorConstants.quoteChar, escapeDelimiter('\\\"'));
            commonDslConnectorProperties.set(DslConnectorConstants.escapeChar, escapeDelimiter('\\\\'));
            commonDslConnectorProperties.set(DslConnectorConstants.rowDelimiter, escapeDelimiter('\\n'));
            commonDslConnectorProperties.set(DslConnectorConstants.fileSystem, 'kafka');
        }
        if (this.isSource) {
            commonDslConnectorProperties.set(DslConnectorConstants.enableCdc, true);
            commonDslConnectorProperties.set(DslConnectorConstants.skipInitialLoad, true);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected valueToResource(object: {}, attribute: IAttribute): any {
        const value = attribute.templateValue(object);
        return value !== undefined ? value : attribute.value(object);
    }

    private _areAllPathsAreUnique(filePaths: IKafkaMapperEntity[]): boolean {
        const seenPaths = new Set<string>();
        for (const path of filePaths) {
            if (seenPaths.has(path.subscribe)) {
                return false;
            }
            seenPaths.add(path.subscribe);
        }
        return true;
    }
}
